/**
 * Iter 265 · Booking Error Telemetry (fires after Jessica Patterson bug).
 *
 * Fire-and-forget helper — any code path that displays a booking-flow error
 * toast, blocks the user at a step, or gets a non-2xx from /api/square/book
 * should call `logBookingError({...})`. We POST the payload to
 * `/api/booking-errors` and never surface a failure to the client.
 *
 * The goal is to catch the "next Jessica" within minutes: her session shows
 * up in `/admin/booking-errors` immediately so Karine can call/text her
 * before she gives up.
 *
 * Usage example inside InlineBookingFlow.jsx:
 *   logBookingError({
 *     code: "auto-prize-select-failed",
 *     message: "No qualifying service matched prize keywords",
 *     step,
 *     location_id: selectedLocation?.id,
 *     service_ids: selectedServices.map(s => s.id),
 *     prize_code: prefillPrizeCode,
 *     prize_title: prefillPrizeTitle,
 *     email: userInfo?.email,
 *     phone: userInfo?.phone,
 *     extra: { catalog_size, best_score },
 *   });
 */

const API = process.env.REACT_APP_BACKEND_URL;

// De-dupe identical errors fired within the same session so we don't
// flood Mongo when a page re-renders 20x and each render fires the same
// toast.
const seen = new Set();

export function logBookingError(payload = {}) {
  try {
    if (!API) return;
    const key = `${payload.code || "unknown"}:${payload.step || ""}:${payload.prize_code || ""}`;
    if (seen.has(key)) return;
    seen.add(key);

    const body = {
      code: payload.code || "unknown",
      message: payload.message || "",
      step: payload.step,
      location_id: payload.location_id,
      service_ids: Array.isArray(payload.service_ids) ? payload.service_ids : [],
      prize_code: payload.prize_code || null,
      prize_title: payload.prize_title || null,
      email: payload.email || null,
      phone: payload.phone || null,
      url: typeof window !== "undefined" ? window.location.href : "",
      extra: payload.extra && typeof payload.extra === "object" ? payload.extra : {},
    };

    // Use sendBeacon when available — survives even if the user closes the
    // tab mid-flight (which is often what "gave up" clients do).
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      navigator.sendBeacon(`${API}/api/booking-errors`, blob);
      return;
    }

    // Fallback — plain fetch, keepalive so the browser doesn't cancel
    // when the modal unmounts on the same tick.
    fetch(`${API}/api/booking-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never let telemetry break the app.
  }
}

export default logBookingError;
